// studio/CodePanel.jsx — Éditeur lecture seule : onglets fichiers, numéros de ligne, coloration, mode Comparer.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ST, ED, PNL } from "@/internal/studio/theme";
import { GitCompareArrows, History, Save, Lock, Plus } from "lucide-react";

const TOKEN = /(\/\/.*$|\/\*[\s\S]*?\*\/|'[^']*'|"[^"]*"|`[^`]*`|\b(?:import|export|from|const|let|var|return|function|default|async|await|if|else|new|map|filter|require|class)\b|\b\d+\b)/g;
const colorOf = (t) => {
  if (t.startsWith("//") || t.startsWith("/*")) return ST.muted;
  if (/^['"`]/.test(t)) return ST.gold;
  if (/^\d+$/.test(t)) return ST.amber;
  return ST.purple;
};
const Line = ({ text }) => (
  <>{text.split(TOKEN).filter((x) => x !== undefined && x !== "").map((tok, i) => (
    TOKEN.lastIndex = 0, TOKEN.test(tok) ? <span key={i} style={{ color: colorOf(tok) }}>{tok}</span> : <span key={i}>{tok}</span>
  ))}</>
);

const mBtn = { display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px", borderRadius: 8, border: `1px solid ${ST.border2}`, background: "transparent", color: ST.muted, fontSize: 11, fontWeight: 800, cursor: "pointer" };

export default function CodePanel({ artifacts, phase, progress, compare, setCompare }) {
  const files = artifacts?.files || [];
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [artifacts?.files?.length, files[0]?.path]);
  const file = files[active];
  const started = phase !== "plan";
  const frac = phase === "coding" ? progress / 100 : started ? 1 : 0;
  const lines = file ? file.code.split("\n") : [];
  const shown = Math.ceil(lines.length * frac);

  return (
    <section style={PNL} data-testid="studio-code-panel">
      <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${ST.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", flex: 1, overflowX: "auto" }}>
          {files.map((f, i) => (
            <button key={f.path} data-testid={`code-tab-${i}`} onClick={() => setActive(i)}
              style={{ padding: "8px 13px", fontSize: 11, fontWeight: 700, fontFamily: ST.mono, border: "none", cursor: "pointer", whiteSpace: "nowrap", background: i === active ? ST.soft : "transparent", color: i === active ? ST.text : ST.muted, borderBottom: `2px solid ${i === active ? ST.green : "transparent"}` }}>
              {f.name} <span style={{ color: ST.muted, marginLeft: 4 }}>×</span>
            </button>
          ))}
          {files.length > 0 && <button style={{ padding: "8px 10px", border: "none", background: "transparent", color: ST.muted, cursor: "pointer" }}><Plus size={13} /></button>}
        </div>
        <div style={{ display: "flex", gap: 6, padding: "0 10px" }}>
          <button data-testid="code-compare" onClick={() => setCompare(!compare)} style={{ ...mBtn, ...(compare ? { color: ST.green, borderColor: "rgba(46,226,123,0.4)" } : {}) }}><GitCompareArrows size={12} /> Comparer</button>
          <button style={mBtn}><History size={12} /> Historique</button>
          <button data-testid="code-save" onClick={() => toast.info("Édition manuelle désactivée — politique AYA : tout changement passe par une intention.", { icon: <Lock size={13} /> })} style={{ ...mBtn, background: "rgba(46,226,123,0.12)", color: ST.green, borderColor: "rgba(46,226,123,0.3)" }}><Save size={12} /> Enregistrer</button>
        </div>
      </div>
      {compare && file && (
        <div style={{ padding: "6px 14px", fontSize: 11, fontFamily: ST.mono, borderBottom: `1px solid ${ST.border}`, color: ST.muted, flexShrink: 0 }}>
          Diff · {file.path} <span style={{ color: ST.green, fontWeight: 800 }}>+{file.added}</span> <span style={{ color: ST.red, fontWeight: 800 }}>−{file.removed}</span>
        </div>
      )}
      <div style={{ flex: 1, overflow: "auto", background: ED.bg }}>
        {!file && <div style={{ padding: 20, color: "#8BA394", fontSize: 12.5 }}>Décrivez une intention pour qu'AYA prépare les fichiers.</div>}
        {file && !started && <div style={{ padding: 20, color: "#8BA394", fontSize: 12.5 }}>Plan prêt — cliquez sur <strong style={{ color: ST.green }}>« Générer le code »</strong> pour lancer les agents.</div>}
        {file && started && (
          <pre data-testid="code-content" style={{ margin: 0, padding: "10px 0", fontSize: 11.5, lineHeight: 1.65, fontFamily: ST.mono, color: ED.text }}>
            {lines.slice(0, shown).map((ln, i) => (
              <div key={i} style={{ display: "flex", background: compare && file.isNew ? "rgba(46,226,123,0.05)" : compare && i < 3 ? "rgba(46,226,123,0.07)" : "transparent" }}>
                <span style={{ width: 42, textAlign: "right", paddingRight: 14, color: "rgba(139,163,148,0.45)", userSelect: "none", flexShrink: 0 }}>{i + 1}</span>
                <span style={{ paddingRight: 16, whiteSpace: "pre" }}><Line text={ln} /></span>
              </div>
            ))}
            {phase === "coding" && <div style={{ paddingLeft: 56 }}><span style={{ display: "inline-block", width: 7, height: 14, background: ST.green, animation: "studioBlink 0.8s step-start infinite" }} /></div>}
          </pre>
        )}
      </div>
    </section>
  );
}
